import { useReactiveVar } from '@apollo/client'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { BackButton } from '@src/components/BackButton/BackButton'
import {
  SingleOutlet,
  SingleOutletDirection,
} from '@src/components/SingleOutlet/types'
import { breakpoints } from '@src/constants/breakpoints'
import { useCategories } from '@src/hooks/useCategories/useCategories'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { screenResolutionVar } from '@src/models/screenResolution'
import { DateifyOutlet } from '@src/utils/fulfilmentTimes/parsers'

import {
  Container,
  ContentContainer,
  HeaderContainer,
  MainContainer,
  NonIdealCategories,
  SubHeader,
  SubheaderAndBackButtonContainer,
} from './CategoryFilteredOutlets.styles'
import { OutletListContainer, StyledOutletCard } from './OutletList.styles'
import { OutletListCompactSkeleton } from './OutletListCompactSkeleton'
import { CategorySideMenu } from '@src/components/Categories/CategorySideMenu'

export const CategoryFilteredOutlets: React.FC<{
  outlets: Array<
    DateifyOutlet<
      Pick<
        SingleOutlet,
        | 'id'
        | 'displayName'
        | 'restaurant'
        | 'availableFulfilmentInputMethods'
        | 'description'
        | 'deliveryMinimumOrderValue'
        | 'collectionMinimumOrderValue'
        | 'isOnline'
        | 'isOpen'
        | 'outletLogoOverride'
        | 'distanceFromUserKM'
        | 'prepTime'
        | 'statusText'
        | 'nextOpenDate'
        | 'availableFulfillmentMethods'
        | 'ASAPDeliveryDuration'
        | 'fulfilmentRange'
        | 'outletAllDiscounts'
        | 'specialOfferFlags'
        | 'outletCuisines'
        | 'outletLoyaltyCards'
      >
    >
  >
  loading?: boolean
}> = ({ outlets, loading = false }) => {
  const { t: commonT } = useTranslation()
  const { t: errorsT } = useTranslation('errors')
  const { width } = useReactiveVar(screenResolutionVar)
  const { urlPath } = useMarketplace()
  const { setCategoryNames } = useCategories()
  const hasMountedRef = useRef(false)

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  return (
    <Container>
      <ContentContainer>
        <HeaderContainer>
          <SubheaderAndBackButtonContainer>
            <BackButton
              onClick={() => setCategoryNames({ categories: undefined })}
            />

            <SubHeader>
              {commonT('category_search_results', {
                urlPath: urlPath.charAt(0).toUpperCase() + urlPath.slice(1),
              })}
            </SubHeader>
          </SubheaderAndBackButtonContainer>

          {!loading && <SubHeader>{outlets.length} Results</SubHeader>}
        </HeaderContainer>

        <MainContainer>
          <CategorySideMenu />

          <OutletListContainer isSearchResults>
            {loading && <OutletListCompactSkeleton segments={false} />}
            {!loading && outlets.length
              ? outlets.map(outlet => (
                  <StyledOutletCard
                    key={`categoryOutlets` + outlet.id}
                    outlet={outlet}
                    direction={
                      width < breakpoints.tablet
                        ? SingleOutletDirection.ROW
                        : SingleOutletDirection.COLUMN
                    }
                    $showingCategorySideMenu
                  />
                ))
              : null}
            {!loading && !outlets.length && (
              <NonIdealCategories>
                {errorsT('no_outlets_categories')}
              </NonIdealCategories>
            )}
          </OutletListContainer>
        </MainContainer>
      </ContentContainer>
    </Container>
  )
}
